// Shared "Shakthi Power Tariff" resolution — same precedence used to pick a
// rate for a generator or a consumer out of Tariff Master. Returns null (not
// 0) when nothing active applies, so callers can distinguish "no tariff
// configured" and block instead of silently billing at ₹0 or a guessed rate.
import type { Tariff } from '@/context/MasterDataContext'

function extractRate(t: Tariff | undefined): number {
  if (!t) return 0
  const rate = Number(t.ratePerUnit || 0)
  if (!isNaN(rate) && rate > 0) return rate
  const pf = Number(t.powerFacilitationCharge || 0)
  if (!isNaN(pf) && pf > 0) return pf
  return 0
}

export function resolveGeneratorTariff(tariffs: Tariff[], generatorId: string): number | null {
  if (!generatorId) return null
  const active = tariffs.filter(t => t.status === 'active')

  const specific = active.find(t => t.generatorId === generatorId)
  if (specific) {
    const rate = extractRate(specific)
    if (rate > 0) return rate
  }

  const general = active.find(t => t.applicableTo === 'generator' || t.applicableTo === 'both')
  if (general) {
    const rate = extractRate(general)
    if (rate > 0) return rate
  }

  return null
}

export function resolveConsumerTariff(tariffs: Tariff[], consumerId: string, generatorId?: string): number | null {
  if (!consumerId) return null
  const active = tariffs.filter(t => t.status === 'active')

  if (generatorId) {
    const genCons = active.find(t => t.generatorId === generatorId && t.consumerId === consumerId)
    if (genCons) {
      const rate = extractRate(genCons)
      if (rate > 0) return rate
    }
  }

  const cons = active.find(t => t.consumerId === consumerId)
  if (cons) {
    const rate = extractRate(cons)
    if (rate > 0) return rate
  }

  if (generatorId) {
    const gen = active.find(t => t.generatorId === generatorId && (t.applicableTo === 'consumer' || t.applicableTo === 'both'))
    if (gen) {
      const rate = extractRate(gen)
      if (rate > 0) return rate
    }
  }

  const general = active.find(t => t.applicableTo === 'consumer' || t.applicableTo === 'both')
  if (general) {
    const rate = extractRate(general)
    if (rate > 0) return rate
  }

  return null
}
